import { ClockIcon, SparklesIcon } from '@heroicons/react/20/solid';
import { twMerge } from 'tailwind-merge';

import Skeleton from '@/components/UI/Skeleton';
import { ArrowTrendingDownIcon, CurrencyDollarIcon } from '@heroicons/react/16/solid';

export const PriceInfo: React.FC<{
  price: number;
  originalPrice: number;
  earlyBird?: number;
  earlyBirdEndDate?: string;
  locale?: string;
  variety?: API.MembershipProductOffer;
  stats?: API.MembershipProductStats;
  discount?: number;
  balance?: number;
  winback?: { price: number; expiresAt?: string | null } | null;
  upgrade?: { price: number; introPaid?: number; eligibleUntil?: string | null } | null;
  loading?: boolean;
}> = ({
  price,
  earlyBird: earlyBirdRaw,
  locale,
  originalPrice,
  variety,
  // stats,
  discount = 0,
  balance = 0,
  winback,
  upgrade,
  earlyBirdEndDate,
  loading = false,
}) => {
  // Per-user NFL win-back price (#1313): a flat campaign price already reflected
  // in `price`. It renders exactly like the sale badge — a red "Save $X · Ends
  // <date>" pill with the campaign price big and the catalog price struck through
  // — so the early-bird pill and the green "Your price" block are both suppressed.
  const hasWinback = !!winback && typeof winback.price === 'number';
  // CFB intro -> season upgrade (#1491): `price` is already the upgraded amount.
  // Render like the win-back markdown (big upgraded price, struck full season) but
  // in the positive "upgrade" framing. Suppressed when win-back also applies (they
  // are mutually exclusive; win-back wins).
  const hasUpgrade = !hasWinback && !!upgrade && typeof upgrade.price === 'number';
  const introPaid = hasUpgrade ? upgrade?.introPaid || 0 : 0;
  // Full season price = the upgraded price plus the intro credit that was applied.
  const upgradeFullPrice = hasUpgrade ? price + introPaid : 0;
  const earlyBird = hasWinback || hasUpgrade ? undefined : earlyBirdRaw;
  const showYourPrice = !hasWinback && !hasUpgrade && (!!discount || !!balance);
  const displayPrice = originalPrice + (variety?.addOn?.price || 0);
  // Anchor the win-back against the full MSRP (catalog price + any early-bird),
  // not the already-discounted current price, so the savings reflect the true
  // markdown (e.g. $750 struck from the $1,200 total, saving $450 — not $250 off
  // the $1,000 sale price). The last full day it is valid (the grant expires at
  // the instant after that day, in Pacific business time) drives the pill date.
  const winbackTotal = displayPrice + (earlyBirdRaw || 0);
  const winbackSave = hasWinback ? Math.max(0, winbackTotal - price) : 0;
  const winbackEnds =
    hasWinback && winback?.expiresAt
      ? new Intl.DateTimeFormat(locale || 'en-US', {
          month: 'short',
          day: 'numeric',
          timeZone: 'America/Los_Angeles',
        }).format(new Date(new Date(winback.expiresAt).getTime() - 1000))
      : '';
  let balanceUsed = Math.min(balance, price);
  let priceSuffix = '';
  const isAnnual = variety?.time?.subscription?.interval === '1 years';
  if (variety?.time?.subscription?.interval) {
    priceSuffix = '/ month';
    balanceUsed = 0; // cannot use balance on Subscriptions
    // @todo https://github.com/parkerrome/raspicks/issues/897
  }

  if (hasUpgrade) {
    return (
      <div className="mb-2">
        <div className="mb-3">
          <p className="inline-flex w-fit items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1">
            <SparklesIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Upgrade ·{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(introPaid)}{' '}
              intro credit
            </span>
          </p>
        </div>
        <div className="flex items-baseline">
          <p className="font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100">
            <Skeleton loading={loading}>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(price)}
              </span>
            </Skeleton>
          </p>
          {upgradeFullPrice > price && (
            <p className="ms-3 font-medium text-xl text-zinc-400 line-through">
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(upgradeFullPrice)}
            </p>
          )}
        </div>
        <p className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
          Upgrade from your Weeks 0-1 intro to the full season.
        </p>
      </div>
    );
  }

  if (hasWinback) {
    return (
      <div className="mb-2">
        <div className="mb-3">
          {/* Mobile: compact single-line pill */}
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-red-500 px-2.5 py-0.5 sm:hidden">
            <ClockIcon height={11} className="size-2.5 shrink-0 text-white" />
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase text-white">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(winbackSave)}
              {winbackEnds && ` · Ends ${winbackEnds}`}
            </span>
          </p>
          {/* sm+: single-line pill badge */}
          <p className="hidden w-fit items-center gap-1.5 rounded-full bg-red-500 px-3 py-1 sm:inline-flex">
            <ClockIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(winbackSave)}
              {winbackEnds && ` · Ends ${winbackEnds}`}
            </span>
          </p>
        </div>
        <div className="flex items-baseline">
          <p className="font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100">
            <Skeleton loading={loading}>
              <span>
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(price)}
              </span>
            </Skeleton>
          </p>
          <p className="ms-3 font-medium text-xl text-zinc-400 line-through">
            {new Intl.NumberFormat(locale, {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
            }).format(winbackTotal)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-2">
      {!!earlyBird && (
        <div className="mb-3">
          {/* Mobile: compact single-line pill */}
          <p className="inline-flex w-fit items-center gap-1 rounded-full bg-red-500 px-2.5 py-0.5 sm:hidden">
            <ClockIcon height={11} className="size-2.5 shrink-0 text-white" />
            <span className="whitespace-nowrap text-[10px] font-semibold uppercase text-white">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}
              {earlyBirdEndDate && ` · Ends ${earlyBirdEndDate}`}
            </span>
          </p>
          {/* sm+: single-line pill badge */}
          <p className="hidden w-fit items-center gap-1.5 rounded-full bg-red-500 px-3 py-1 sm:inline-flex">
            <ClockIcon height={14} className="size-3.5 shrink-0 text-white" />
            <span className="whitespace-nowrap font-semibold text-white text-xs uppercase tracking-wide">
              Save{' '}
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(earlyBird)}
              {earlyBirdEndDate && ` · Ends ${earlyBirdEndDate}`}
            </span>
          </p>
        </div>
      )}
      <div className="flex items-baseline">
        {!!earlyBird && (
          <p
            className={twMerge(
              'font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100',
              showYourPrice && 'font-normal text-xl text-zinc-500 line-through dark:text-zinc-300',
            )}
          >
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: 0,
              }).format(displayPrice)}
            </span>
            {priceSuffix && (
              <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
                {priceSuffix}
              </span>
            )}
          </p>
        )}
        <p
          className={twMerge(
            'font-extrabold text-4xl tracking-tight text-zinc-900 sm:text-5xl dark:text-zinc-100',
            earlyBird && 'text-xl! font-medium! text-zinc-400! ms-3 line-through',
            showYourPrice && 'font-normal text-xl text-zinc-500 dark:text-zinc-300',
          )}
        >
          <Skeleton loading={loading}>
            <span className={twMerge(showYourPrice && 'line-through')}>
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(
                isAnnual ? (displayPrice + (earlyBird || 0)) / 12 : displayPrice + (earlyBird || 0),
              )}
            </span>
          </Skeleton>
          {priceSuffix && (
            <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
              {priceSuffix}
            </span>
          )}
        </p>
      </div>
      {showYourPrice && (
        <div className="mt-4 text-emerald-700 dark:text-emerald-100">
          <p className="flex flex-row space-x-1 font-medium text-sm">
            <ArrowTrendingDownIcon height={16} className="size-4" />
            <span>
              Your price
              {!!discount && ` (${discount * 100}% off)`}
            </span>
          </p>
          <p className="flex flex-row items-baseline space-x-2 font-semibold text-3xl tracking-tight">
            <span>
              {new Intl.NumberFormat(locale, {
                style: 'currency',
                currency: 'USD',
                maximumFractionDigits: isAnnual ? 2 : 0,
              }).format(isAnnual ? (price - balanceUsed) / 12 : price - balanceUsed)}
            </span>
            {priceSuffix && (
              <span className="ml-1 font-normal text-semibold text-sm text-zinc-600 leading-6 tracking-normal dark:text-zinc-400">
                {priceSuffix}
              </span>
            )}
          </p>
          {!!balance && !!balanceUsed && (
            <div className="mt-2 flex flex-row items-center space-x-1 font-medium text-xs">
              <CurrencyDollarIcon height={16} className="size-4 shrink-0" />
              <span>
                With{' '}
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(balanceUsed)}{' '}
                of your{' '}
                {new Intl.NumberFormat(locale, {
                  style: 'currency',
                  currency: 'USD',
                  maximumFractionDigits: 0,
                }).format(balance)}{' '}
                balance applied
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
